/**
 * BlogIndex — /blog landing page.
 *
 * iter100 (P9): hub for long-form SEO content. Pulls posts from the
 * static registry `blog/posts.js` so Google + crawlers see the full
 * list of articles on first paint without any JS-fetched data.
 *
 * Empty-state today (no articles yet) — the owner will seed content
 * later. The structure is fully wired so each new post automatically
 * gets a card here, a per-post SEO/JSON-LD page, and a sitemap entry.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import Navbar from '../../components/Navbar';
import FooterSection from '../../components/FooterSection';
import SEO from '../../components/SEO';
import { LanguageProvider } from '../../context/LanguageContext';
import { BLOG_POSTS } from './posts';

const BlogIndex = () => {
  const posts = BLOG_POSTS;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': 'https://nofee.restaurant/blog#blog',
    name: 'NoFee Blog',
    url: 'https://nofee.restaurant/blog',
    description:
      'Guides and insights for restaurant owners — direct ordering, profit margins, delivery economics, and how to break free from marketplace commissions.',
    publisher: { '@id': 'https://nofee.restaurant/#org' },
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `https://nofee.restaurant/blog/${p.slug}`,
      datePublished: p.datePublished,
      author: { '@type': 'Organization', name: p.author || 'NoFee Team' },
    })),
  };

  return (
    <LanguageProvider>
      <SEO
        title="NoFee Blog — Restaurant Profitability, Direct Ordering & Delivery Economics"
        description="Long-form guides for US restaurant owners. Learn how to break free from marketplace commissions, build direct ordering, and grow margins — by the NoFee team."
        path="/blog"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-black text-white antialiased" data-testid="blog-index">
        <Navbar forceSolid />

        <main className="pt-28 lg:pt-36 pb-20">
          <div className="max-w-[1080px] mx-auto px-6 lg:px-8">
            {/* Hero */}
            <div className="mb-12 lg:mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/[0.08] border border-gold-500/25 text-[11.5px] uppercase tracking-[0.22em] text-gold-300 font-bold mb-5">
                <BookOpen className="w-3.5 h-3.5" /> NoFee Blog
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.02em] text-white leading-[1.05]" data-testid="blog-index-title">
                Insights for restaurants who want to keep{' '}
                <span className="bg-gradient-to-b from-amber-200 to-gold-400 bg-clip-text text-transparent">
                  more of every order
                </span>.
              </h1>
              <p className="mt-4 text-white/65 text-base lg:text-lg max-w-[640px] leading-relaxed">
                Long-form guides on direct ordering, profit margins, delivery economics, and the future of restaurant commerce — written by the NoFee team.
              </p>
            </div>

            {posts.length === 0 ? (
              <div
                className="rounded-3xl border border-dashed border-white/[0.10] bg-white/[0.015] p-12 text-center"
                data-testid="blog-index-empty"
              >
                <Sparkles className="w-7 h-7 text-gold-300 mx-auto mb-3" />
                <p className="text-lg font-bold text-white mb-2">Fresh articles dropping soon.</p>
                <p className="text-sm text-white/55 max-w-[480px] mx-auto leading-relaxed">
                  We&rsquo;re wrapping up our launch series. In the meantime,
                  see how much your restaurant could save with our calculator.
                </p>
                <Link
                  to="/savings-calculator"
                  className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold text-[13.5px] transition-all"
                  data-testid="blog-index-cta-calc"
                >
                  Open the Savings Calculator <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-index-grid">
                {posts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="group rounded-2xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.04] hover:border-gold-500/40 overflow-hidden transition-all"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    {p.cover && (
                      <div className="aspect-[16/9] overflow-hidden bg-zinc-900">
                        <img
                          src={p.cover}
                          alt={p.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      {p.tag && (
                        <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold mb-2">
                          {p.tag}
                        </p>
                      )}
                      <h2 className="text-[16px] font-bold text-white leading-snug group-hover:text-gold-200 transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-2 text-[13px] text-white/55 line-clamp-3 leading-relaxed">{p.description}</p>
                      <p className="mt-3 text-[11px] text-white/40">
                        {p.readTimeMin ? `${p.readTimeMin} min read` : ''}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>

        <FooterSection />
      </div>
    </LanguageProvider>
  );
};

export default BlogIndex;
